import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { withTranslation } from '../../../ChildrenContext.js';
import PropTypes from 'prop-types';
import moment from 'moment';
import StatisticsByRefererFilter from '../StatisticsByRefererFilter';
import StatisticsByRefererTableComponent from '../StatisticsByRefererTable/StatisticsByRefererTable.container';
import StatisticsByRefererMetricsComponent from '../StatisticsByRefererMetrics/StatisticsByRefererMetrics.container';
import StatisticsByRefererSegmentsComponent from '../StatisticsByRefererSegments/StatisticsByRefererSegments.container';
import Pagination from '../../../Pagination';
import StatisticsByRefererCSV from '../StatisticsByRefererCSV/StatisticsByRefererCSV.container';
import config from '../../../../config';
import objectHasChanged from '../../../../helpers/objectHasChanged';
import query from '../../../../helpers/query';
import { sanitizeSegments, areSegmentsEqual } from '../../../../helpers/segmentUtils';
import { isSameFilterValues } from '../../../../helpers/reportFilterReset';
import ReportByRefererComponent from '../../../../utils/ReportByRefererComponent/ReportByRefererComponent';

const defDataSort = {
    sort: 'GroupBy',
    order: 'desc'
};

import Loading from '../../../Loading';

const StatisticsByRefererContent = ({ 
    location, 
    metrics: propMetrics, 
    segments: propSegments, 
    page, 
    totalPages, 
    loadingData, 
    search, 
    t,
    serverStorageReady
}) => {
    const getDateFromString = useCallback((date) => {
        if (date) {
            date = new Date(date);
            return moment().set({'year': date.getFullYear(), 'month': date.getMonth(), 'date': date.getDate()});
        }
        return false;
    }, []);

    const parsedQuery = useMemo(() => query.parse(location?.query), [location?.query]);
    const reportUtils = ReportByRefererComponent.utils;
    const hasQueryMetrics = Boolean(parsedQuery.metrics);
    const defaultMetricsRef = useRef(reportUtils.getDefStrMetric());

    const sanitizedInitialSegments = useMemo(
        () => sanitizeSegments(parsedQuery.segments || propSegments),
        [parsedQuery.segments, propSegments]
    );

    const [state, setState] = useState(() => {
        let startDate = getDateFromString(parsedQuery.startDate);
        if (startDate) {
            startDate = startDate.startOf('day');
        } else {
            startDate = moment().subtract(6, 'days').startOf('day');
        }

        let endDate = getDateFromString(parsedQuery.endDate);
        if (endDate) {
            endDate = endDate.endOf('day');
        } else {
            endDate = moment().endOf('day');
        }

        return {
            startDate: startDate,
            endDate: endDate,
            bindActions: parsedQuery.bindActions || config.defaultBindActions,
            groupBy: parsedQuery.groupBy || config.defaultGroupBy,
            count: parsedQuery.count || config.defaultCount,
            metrics: parsedQuery.metrics || propMetrics || '',
            sort: parsedQuery.sort || defDataSort.sort,
            order: parsedQuery.order || defDataSort.order,
            segments: sanitizedInitialSegments,
            page: parsedQuery.page || +page,
            totalPages: totalPages,
            loadingData: loadingData
        };
    });

    const isStateSearchChanged = useMemo(() => 
        objectHasChanged(['metrics', 'page', 'totalPages', 'loadingData'], true, state), 
        [state]
    );

    const segmentsStringifiedRef = useRef(JSON.stringify(state.segments));
    const currentSegmentsStr = JSON.stringify(state.segments);
    
    if (segmentsStringifiedRef.current !== currentSegmentsStr) {
        segmentsStringifiedRef.current = currentSegmentsStr;
    }
    
    const segmentsStringified = segmentsStringifiedRef.current;

    const stateRef = useRef(state);
    const searchRef = useRef(search);
    
    useLayoutEffect(() => {
        stateRef.current = state;
        searchRef.current = search;
    }, [state, search]);

    const callSearch = useCallback((params) => {
        if (!params.metrics) {
            return;
        }
        searchRef.current && searchRef.current(params);
    }, []);

    const pushQuery = useCallback(() => {
        const current = stateRef.current;
        query.replace({
            startDate: current.startDate.clone().startOf('day').format(config.reportHourFormat),
            endDate: current.endDate.clone().endOf('day').format(config.reportHourFormat),
            groupBy: current.groupBy || null,
            count: current.count || null,
            bindActions: current.bindActions,
            sort: current.sort,
            order: current.order,
            metrics: current.metrics || null,
            page: current.page,
            segments: current.segments && Object.keys(current.segments || {}).length ? JSON.stringify(current.segments) : null
        });
    }, []);

    const onUpdateDate = useCallback((index) => {
        const current = stateRef.current;
        if (current.loadingData) {
            return;
        }

        const isStateChanged = objectHasChanged(['metrics', 'page', 'totalPages', 'loadingData'], true, current);

        setState(prevState => {
            const next = {
                ...prevState,
                page: isStateChanged ? config.initialPage : prevState.page,
                ...defDataSort
            };
            stateRef.current = next;
            setTimeout(() => {
                const updated = stateRef.current;
                callSearch(updated);
                pushQuery();
            }, 0);
            return next;
        });
    }, [pushQuery, callSearch]);

    // skips the echo of a reset from the top filter (see helpers/reportFilterReset)
    const resetEchoRef = useRef(null);

    const onActionFilter = useCallback((filterState) => {
        const resetValues = resetEchoRef.current;
        resetEchoRef.current = null;
        if (resetValues && isSameFilterValues(resetValues, filterState)) {
            return;
        }

        const current = stateRef.current;
        const isStateChanged = objectHasChanged(['metrics', 'page', 'totalPages', 'loadingData'], true, current);

        setState(prevState => {
            const next = {
                ...prevState,
                page: isStateChanged ? config.initialPage : prevState.page,
                ...filterState
            };
            stateRef.current = next;
            setTimeout(() => {
                const updated = stateRef.current;
                callSearch(updated);
                pushQuery();
            }, 0);
            return next;
        });
    }, [pushQuery, callSearch]);

    // Reset all: top filters and filter rows to defaults, one request
    const onResetAll = useCallback(() => {
        const defaults = {
            startDate: moment().subtract(6, 'days').startOf('day'),
            endDate: moment().endOf('day'),
            bindActions: config.defaultBindActions,
            groupBy: config.defaultGroupBy,
            count: config.defaultCount
        };

        onActionFilter({ ...defaults, segments: {}, page: config.initialPage, ...defDataSort });
        resetEchoRef.current = defaults;
    }, [onActionFilter]);

    const onActionMetrics = useCallback((newMetrics) => {
        setState(prevState => {
            const next = { ...prevState, metrics: newMetrics };
            stateRef.current = next;
            setTimeout(() => {
                const updated = stateRef.current;
                callSearch(updated);
                pushQuery();
            }, 0);
            return next;
        });
    }, [pushQuery, callSearch]);

    const onActionTable = useCallback((dataSort) => {
        setState(prevState => {
            const next = { ...prevState, ...dataSort };
            stateRef.current = next;
            setTimeout(() => {
                const updated = stateRef.current;
                callSearch(updated);
                pushQuery();
            }, 0);
            return next;
        });
    }, [pushQuery, callSearch]);

    const onPageChange = useCallback((newPage) => {
        setState(prevState => {
            const next = { ...prevState, page: newPage };
            stateRef.current = next;
            setTimeout(() => {
                const updated = stateRef.current;
                callSearch(updated);
                pushQuery();
            }, 0);
            return next;
        });
    }, [pushQuery, callSearch]);

    const onActionSegments = useCallback((nextSegments) => {
        setState(prevState => {
            const sanitized = sanitizeSegments(nextSegments);
            if (areSegmentsEqual(prevState.segments, sanitized)) {
                return prevState;
            }

            return {
                ...prevState,
                segments: sanitized
            };
        });
    }, []);

    useEffect(() => {
        setState(prevState => {
            const next = {
                ...prevState,
                totalPages: totalPages,
                loadingData: loadingData
            };
            stateRef.current = next;
            return next;
        });
    }, [totalPages, loadingData]);

    useEffect(() => {
        setState(prevState => {
            if (areSegmentsEqual(prevState.segments, sanitizedInitialSegments)) {
                return prevState;
            }

            return {
                ...prevState,
                segments: sanitizedInitialSegments
            };
        });
    }, [sanitizedInitialSegments]);

    useEffect(() => {
        if (hasQueryMetrics) {
            return;
        }

        if (typeof propMetrics === 'string' && propMetrics.length) {
            setState(prevState => {
                if (prevState.metrics === propMetrics) {
                    return prevState;
                }
                const next = {
                    ...prevState,
                    metrics: propMetrics
                };
                stateRef.current = next;
                return next;
            });
            return;
        }

        if (serverStorageReady && !state.metrics) {
            setState(prevState => {
                if (prevState.metrics) {
                    return prevState;
                }
                const next = {
                    ...prevState,
                    metrics: defaultMetricsRef.current
                };
                stateRef.current = next;
                return next;
            });
        }
    }, [propMetrics, serverStorageReady, hasQueryMetrics, state.metrics]);

    // Initial search: every report page renders its filter only when the metrics are ready (Loading otherwise),
    // and the filter calls onAction once on mount -> exactly one request on page load. No extra initial search here.


    return state.metrics
        ? (
            <section className="wrapper-content statistics">
                <div className="ibox">
                    <StatisticsByRefererFilter
                    startDate={state.startDate}
                    endDate={state.endDate}
                    groupBy={state.groupBy}
                    count={state.count}
                    bindActions={state.bindActions}
                    onAction={onActionFilter}
                    loading={state.loadingData}
                />

                <StatisticsByRefererSegmentsComponent
                    segments={state.segments}
                    onAction={onActionSegments}
                    onUpdateDate={onUpdateDate}
                    loading={state.loadingData}
                    onReset={onResetAll}
                    commonControls={true}
                />
            </div>

            <div className="ibox-title bold" id="statistics-table-header">
                <StatisticsByRefererMetricsComponent onAction={onActionMetrics} metrics={state.metrics}/>
                <StatisticsByRefererCSV/>
            </div>

            <div className="ibox">
                <div className="ibox-content no-padding">
                    <StatisticsByRefererTableComponent
                        sort={state.sort}
                        order={state.order}
                        metrics={state.metrics}
                        onAction={onActionTable}
                    />
                </div>
                <div className="ibox-footer pl0 pr0 pb0">
                    <Pagination
                        onPageChange={onPageChange}
                        initialPage={state.page}
                        totalPages={totalPages}
                        loading={state.loadingData}
                        itemsCountPerPage={state.count}
                        align={'left'}
                    />
                </div>
                </div>
            </section>
        )
        : (
            <section className="wrapper-content statistics">
                <div className="ibox text-center">
                    <Loading center />
                </div>
            </section>
        );
};

StatisticsByRefererContent.propTypes = {
    location: PropTypes.object,
    metrics: PropTypes.string,
    segments: PropTypes.object,
    page: PropTypes.number,
    totalPages: PropTypes.number,
    loadingData: PropTypes.bool,
    search: PropTypes.func,
    t: PropTypes.object,
    serverStorageReady: PropTypes.bool
};

StatisticsByRefererContent.defaultProps = {
    metrics: '',
    segments: {},
    page: 1,
    totalPages: 0,
    loadingData: false,
    search: () => {},
    serverStorageReady: false
};

export default withTranslation(StatisticsByRefererContent);
